import { useMemo } from 'react';

const TILE_CONFIG = [
  {
    key: "overdue",
    label: "Overdue",
    color: "text-red-600",
    bgColor: "bg-red-50",
    iconColor: "text-red-500",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    dest: "Overdue Report",
    emptyLabel: "On track",
    emptyColor: "text-emerald-600",
  },
  {
    key: "blocked",
    label: "Blocked",
    color: "text-red-500",
    bgColor: "bg-red-50",
    iconColor: "text-red-400",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
      </svg>
    ),
    dest: "Blocked Report",
    emptyLabel: "Clear",
    emptyColor: "text-emerald-600",
  },
  {
    key: "pending",
    label: "Pending Approval",
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    iconColor: "text-amber-500",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" />
      </svg>
    ),
    dest: "Pending Tasks",
    emptyLabel: "None",
    emptyColor: "text-slate-400",
  },
  {
    key: "punchList",
    label: "Punch List",
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    iconColor: "text-blue-500",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" /><path d="M9 14l2 2 4-4" />
      </svg>
    ),
    dest: "Punch List",
    emptyLabel: "None",
    emptyColor: "text-slate-400",
  },
  {
    key: "projects",
    label: "Projects",
    color: "text-[#0f2d5c]",
    bgColor: "bg-slate-100",
    iconColor: "text-[#0f2d5c]",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
    dest: "Projects Tab",
  },
];

export default function DashboardKpiTiles({ projects, onNavigate }) {
  const counts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let overdue = 0, blocked = 0, pending = 0, punchList = 0;
    for (const p of projects) {
      for (const t of (p.tasks || [])) {
        if (t.isPhaseHeader) continue;
        if (t.status === "Blocked") blocked++;
        if (t.status === "Pending Approval") pending++;
        if (t.isPunchList && t.status !== "Complete") punchList++;
        if (t.status !== "Complete" && t.dueDate) {
          const due = new Date(t.dueDate);
          due.setHours(0, 0, 0, 0);
          if (due < today) overdue++;
        }
      }
    }
    return { overdue, blocked, pending, punchList, projects: projects.length };
  }, [projects]);

  function handleClick(key) {
    if (key === "overdue") onNavigate("overdue-blocked");
    else if (key === "blocked") onNavigate("overdue-blocked");
    else if (key === "pending") onNavigate("pending");
    else if (key === "punchList") onNavigate("punchList");
    else if (key === "projects") onNavigate("projects-tab");
  }

  return (
    <div className="flex flex-wrap gap-3">
      {TILE_CONFIG.map(tile => (
        <button key={tile.key} onClick={() => handleClick(tile.key)}
          className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 min-w-[140px] flex-1 cursor-pointer hover:border-amber-400 hover:shadow-sm transition-all text-left relative group">
          <div className={`w-9 h-9 rounded-lg ${tile.bgColor} flex items-center justify-center flex-shrink-0`}>
            <span className={tile.iconColor}>{tile.icon}</span>
          </div>
          <div>
            {counts[tile.key] === 0 && tile.emptyLabel ? (
              <p className={`text-sm font-semibold ${tile.emptyColor}`}>{tile.emptyLabel}</p>
            ) : (
              <p className={`text-2xl font-bold ${tile.color}`}>{counts[tile.key]}</p>
            )}
            <p className="text-xs text-slate-500">{tile.label}</p>
          </div>
          <span className="absolute top-1 right-2 text-[10px] font-semibold text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded hidden group-hover:inline-flex items-center gap-0.5">
            → {tile.dest}
          </span>
        </button>
      ))}
    </div>
  );
}
